import React, { useMemo } from "react";
import ScreenCard from "../ScreenCard";
import { CreateMissionScreensContext } from "../../contexts/CreateMissionManager";
import { useFormatContractWorkingTime, useI18n } from "../../hooks/i18n-hooks";
import { useRecoilValue } from "recoil";
import { newMissionState } from "../../atoms/core";
import { useNavigation } from "@react-navigation/native";
import { useSnackbar } from "../../hooks/snackbar";
import { Contract, Mission, ProfileType } from "../../__generated__/graphql";
import ListItem from "../../ui_atoms/ListItem";
import { useContractDisplayAttributes } from "../../hooks/missions";
import SalaryProjections from "../../components/SalaryProjections";
import Text from "../../components/Text";
import { useTheme } from "../../hooks/use-theme";
import { StyleSheet, View } from "react-native";
import Gap from "../../ui_atoms/Gap";
import { useContractCct } from "../../hooks/cct-utils";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import { useCurrentProfile } from "../../hooks/use-current-profile";
import { useBuildIjmDescription } from "../../hooks/ijm";
import { SHORT_DATE_FORMAT_OPTIONS, useDateFormatter } from "../../hooks/date";
import { TitleMediumText } from "../../components/FormattedText";
import VStack from "../../ui_atoms/VStack";
import ListGroup from "../../components/ListGroup";
import { useIntercom } from "../../hooks/intercom";
import { useBubbleStepOrigin } from "../../hooks/use-bubble-step-origin";
import { Bubble } from "../../components/Bubble";

const CreateMissionSummary = () => {
    const i18n = useI18n();
    const styles = useStyles();
    const { gridSize } = useTheme();
    const navigation = useNavigation();
    const { presentArticleId } = useIntercom();
    const { validateScreen } = React.useContext(CreateMissionScreensContext);
    const [processing, setProcessing] = React.useState(false);
    const currentMission = useRecoilValue(newMissionState);
    const currentProfile = useCurrentProfile();
    const isPrivateClient = currentProfile?.isPrivateClient || false;
    const { profileType } = React.useContext(ProfileSwitcherManagerContext);
    const { showSnackbar } = useSnackbar();
    const formatDate = useDateFormatter();
    const formatContractWorkingTime = useFormatContractWorkingTime();
    const missionCct = useContractCct(currentMission.currentContract as Contract);
    const buildIjmDescription = useBuildIjmDescription();

    const validate = React.useCallback(async () => {
        setProcessing(true);
        try {
            await validateScreen("CreateMissionSummary");
        } catch (err) {
            showSnackbar({
                mode: "error",
                text: "Error creating mission",
            });
            console.warn("error saving mission", err);
        } finally {
            setProcessing(false);
        }
    }, [showSnackbar, validateScreen]);

    React.useEffect(() => {
        const unsubscribe = navigation.addListener("focus", () => {
            setProcessing(false);
        });

        // Return the function to unsubscribe from the event so it gets removed on unmount
        return unsubscribe;
    }, [navigation, setProcessing]);
    const getDisplayAttributes = useContractDisplayAttributes();

    const displayAttributes = useMemo(
        () => getDisplayAttributes(currentMission?.currentContract as Contract, profileType),
        [getDisplayAttributes, currentMission?.currentContract, profileType],
    );

    const origin = useBubbleStepOrigin("CreateMissionSummary");

    return (
        <ScreenCard
            buttons={[
                {
                    title: i18n.translate("screens.user.create_mission_summary.next_title"),
                    onPress: validate,
                    mode: "secondary",
                    validating: processing,
                },
            ]}
        >
            <Bubble
                title={i18n.t("screens.user.create_mission_summary.title")}
                origin={origin}
                bubbleStyle={{ paddingBottom: 0 }}
            >
                {profileType && currentMission.currentContract && (
                    <>
                        <Text style={styles.description}>
                            {i18n.t("screens.user.create_mission_summary.description")}
                        </Text>
                        <View style={{ marginHorizontal: -(gridSize / 2) }}>
                            <Gap gap="1/2" />
                            <ListItem
                                title={i18n.t("screens.user.contract_details.legal_employer")}
                                description={currentMission?.legalEmployer?.marketingName || ""}
                            />
                            <ListItem
                                title={i18n.t("screens.user.contract_details.mission_employer")}
                                description={currentMission?.clientInfo?.name || ""}
                            />
                            <ListItem
                                title={i18n.t("screens.user.contract_details.talent")}
                                description={currentMission?.workerInfo?.name || ""}
                            />
                            <ListItem
                                title={i18n.t("screens.user.contract_details.address")}
                                description={
                                    currentMission?.currentContract?.workingAddress?.formatted || ""
                                }
                            />
                            <ListItem
                                title={i18n.t("screens.user.contract_details.job_title")}
                                description={[
                                    currentMission.activityType?.title,
                                    ...(currentMission.activityType?.title !==
                                    currentMission?.currentContract?.jobTitle
                                        ? [currentMission?.currentContract?.jobTitle]
                                        : []),
                                ].join("\n")}
                            />
                            {currentMission?.currentContract?.jobDescription && (
                                <ListItem
                                    title={i18n.t("screens.user.contract_details.job_description")}
                                    description={currentMission?.currentContract?.jobDescription}
                                />
                            )}
                            {!isPrivateClient && (
                                <ListItem
                                    title={i18n.t("screens.user.contract_details.cct")}
                                    description={missionCct?.name || ""}
                                />
                            )}
                            {currentMission.currentContract?.startDate && (
                                <ListItem
                                    inGroup={profileType === ProfileType.Worker}
                                    title={i18n.t("screens.user.contract_details.start_date")}
                                    description={formatDate(
                                        new Date(currentMission.currentContract.startDate),
                                        SHORT_DATE_FORMAT_OPTIONS,
                                    )}
                                />
                            )}
                            <ListItem
                                title={i18n.t("screens.user.contract_details.end_date")}
                                description={
                                    currentMission.currentContract?.endDate
                                        ? formatDate(
                                              new Date(currentMission.currentContract.endDate),
                                              SHORT_DATE_FORMAT_OPTIONS,
                                          )
                                        : i18n.t("screens.user.contract_details.no_end_date")
                                }
                            />
                            <ListItem
                                title={i18n.t(`utils.working_time.classic.title`)}
                                description={formatContractWorkingTime(
                                    currentMission.currentContract,
                                )}
                            />
                            <ListItem
                                title={i18n.t("mission.vacationDays")}
                                description={displayAttributes.vacationDays}
                            />

                            <ListItem
                                title={i18n.t("mission.payrollSource")}
                                description={i18n.t(
                                    `payrollSource.type.${currentMission.payrollSource}`,
                                )}
                            />
                            {currentMission.ijmSelection && (
                                <ListItem
                                    title={i18n.t("ijm.mission.title")}
                                    description={buildIjmDescription(
                                        currentMission.ijmSelection.ijmGroupName || undefined,
                                        currentMission.ijmSelection.ijmPayFirstDays,
                                        currentMission.ijmSelection.ijmPayRemainingPercents,
                                    )}
                                />
                            )}
                            <ListItem
                                title={i18n.t("mission.invoiced")}
                                description={displayAttributes.salaryInvoice}
                            />
                            <ListItem
                                title={i18n.t("mission.gross_salary")}
                                description={displayAttributes.salaryGross}
                            />
                            {displayAttributes.fees?.map((fee) => (
                                <ListItem
                                    key={fee.key}
                                    title={fee.title}
                                    description={fee.description}
                                />
                            ))}
                            {displayAttributes.deductions?.map((deduction) => (
                                <ListItem
                                    key={deduction.key}
                                    title={deduction.title}
                                    description={deduction.description}
                                />
                            ))}
                            <ListItem inGroup title={i18n.t("mission.salary_projections")} />
                            <SalaryProjections mission={currentMission as Mission} />
                            <Gap gap={1} />
                            <VStack h="2/3" gap={1}>
                                <TitleMediumText>{i18n.t("mission.good_to_know")}</TitleMediumText>
                            </VStack>
                            <Gap gap="2/3" />
                            <ListGroup
                                items={[
                                    {
                                        description: i18n.t("mission.how_mission_work_for_clients"),
                                        onPress: () => {
                                            void presentArticleId("9983409");
                                        },
                                        value: "",
                                    },
                                    {
                                        description: i18n.t("mission.how_payroll_breakdown_works"),
                                        onPress: () => {
                                            void presentArticleId("8708309");
                                        },
                                        value: "",
                                    },
                                    {
                                        description: i18n.t("mission.how_talent_salary_is_paid"),
                                        onPress: () => {
                                            void presentArticleId("9983327");
                                        },
                                        value: "",
                                    },
                                    {
                                        description: i18n.t("mission.what_employee_do_if_absent"),
                                        onPress: () => {
                                            void presentArticleId("9983703");
                                        },
                                        value: "",
                                        style: { borderBottomWidth: 0 },
                                    },
                                ]}
                            />
                        </View>
                    </>
                )}
            </Bubble>
        </ScreenCard>
    );
};

export default CreateMissionSummary;

const useStyles = () => {
    const { fonts, colors } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                description: {
                    ...fonts.bodyMedium,
                },
                info: {
                    ...fonts.bodyMedium,
                    color: colors.onSurfaceDisabled,
                },
            }),
        [fonts.bodyMedium, colors.onSurfaceDisabled],
    );
};
